/**
 * Écran de fin d'essai / de suspension — LOT F3, cycle de vie côté utilisateur.
 *
 * ★ D'où vient l'information — et pourquoi elle vient d'une route PUBLIQUE.
 *
 * Un tenant `expire` ou `suspendu` fait échouer **toute** requête avec
 * `TENANT_EXPIRE` / `TENANT_SUSPENDU` (`plateforme/resolution.ts`), avant même
 * la garde de rôle. On réagit donc à cette erreur — mais il faut bien qu'une
 * requête parte, et elle doit partir depuis les TROIS coques, y compris celle
 * de l'abonné.
 *
 * Ce fichier interrogeait `GET /configuration/theatre`, réservée au rôle
 * `admin` : un abonné connecté par lien magique n'a pas d'`utilisateur`, la
 * garde répondait 401, et `lib/api.ts` traduit un 401 en « session expirée ».
 * Le portail se déconnectait donc tout seul, à chaque ouverture. La sonde est
 * désormais `GET /theatre/publique` (`lib/theatre.ts`), sans session — la même
 * que celle des en-têtes, donc dédupliquée par TanStack Query.
 *
 * Cette route rend aussi `expireLe` sur un tenant actif : le bandeau proactif
 * « votre essai se termine dans X jours » (discret à J-30) n'a plus de
 * dépendance manquante, seulement un écran à écrire.
 *
 * ★ « refuse les écritures mais laisse consulter » (RESTE-A-FAIRE, F3) — non
 * implémenté ici, et volontairement : `plateforme/resolution.ts` refuse
 * AUJOURD'HUI toute requête (lecture comprise) sur un tenant `expire` ou
 * `suspendu`, avant la moindre distinction GET/mutation. Changer cela suppose
 * de modifier `resolution.ts`/`hooks.ts`, hors du territoire de ce lot — donc
 * hors du périmètre de ce fichier, qui reflète fidèlement le comportement
 * actuel (blocage total) plutôt que de promettre une consultation qui
 * échouerait à l'écran suivant.
 */

import { ApiError } from '../lib/api'
import { intensiteEcheance, joursRestants, messageEcheance } from '../lib/echeance'
import { useTheatrePublique } from '../lib/theatre'
import { Button, classesBouton } from '../ui/Button'

export type EtatTenantBloquant =
  | { readonly genre: 'expire'; readonly message: string; readonly expireLe: string | undefined }
  | { readonly genre: 'suspendu'; readonly message: string; readonly motif: string | undefined }

/**
 * Détecte, depuis n'importe quelle coque, que le théâtre servi est expiré ou
 * suspendu.
 *
 * S'appuie sur `useTheatrePublique()` — la MÊME requête que celle des en-têtes
 * (`useTheatre()`), donc dédupliquée par TanStack Query et jamais répétée,
 * quel que soit le nombre de coques qui l'appellent.
 *
 * ★ Ne rend jamais `undefined` par accident sur un 401 ou un 403 : la route est
 * publique, elle n'a aucune garde de rôle. Le seul refus qu'elle puisse
 * produire est celui de la plateforme — exactement celui qu'on cherche.
 */
export function useEtatTenantBloquant(): EtatTenantBloquant | undefined {
  const requete = useTheatrePublique()

  const erreur = requete.error
  if (!(erreur instanceof ApiError)) return undefined

  if (erreur.code === 'TENANT_EXPIRE') {
    const expireLe = erreur.details?.['expireLe']
    return {
      genre: 'expire',
      message: erreur.message,
      expireLe: typeof expireLe === 'string' ? expireLe : undefined,
    }
  }
  if (erreur.code === 'TENANT_SUSPENDU') {
    const motif = erreur.details?.['motif']
    return {
      genre: 'suspendu',
      message: erreur.message,
      motif: typeof motif === 'string' ? motif : undefined,
    }
  }
  return undefined
}

/* -------------------------------------------------------------------------- */
/* Le bandeau proactif — RESTE-A-FAIRE, lot F point A                        */
/* -------------------------------------------------------------------------- */

/**
 * Jours restants avant l'échéance d'une démonstration, tant que le bandeau
 * doit se montrer — `undefined` sinon (plus de 30 jours restants, tenant
 * `client` sans terme, ou déjà bloqué : voir `lib/echeance.ts`).
 *
 * Même requête que `useEtatTenantBloquant()` et `useTheatre()`
 * (`CLE_THEATRE_PUBLIQUE`, dédupliquée par TanStack Query) : ce hook ne coûte
 * jamais un appel réseau de plus, quelle que soit la coque qui le monte.
 */
export function useEcheanceTenant(): number | undefined {
  const requete = useTheatrePublique()
  const expireLe = requete.data?.expireLe
  if (expireLe === undefined || expireLe === null) return undefined

  const jours = joursRestants(expireLe)
  return intensiteEcheance(jours) === undefined ? undefined : jours
}

/**
 * Quatre paliers de PRÉSENTATION lus sur une intensité qui, elle, reste
 * continue (`lib/echeance.ts`) — la gradation n'est jamais un simple
 * booléen « discret / insistant », mais la CSS d'un utilitaire Tailwind ne
 * s'interpole pas en continu : ces quatre crans sont la meilleure
 * approximation d'un dégradé que les classes utilitaires du reste de
 * l'application permettent, du même geste que le reste de l'interface
 * plutôt qu'une couleur calculée en `style` inline.
 */
function palierEcheance(intensite: number): 'discret' | 'modere' | 'marque' | 'insistant' {
  if (intensite < 0.3) return 'discret'
  if (intensite < 0.6) return 'modere'
  if (intensite < 0.85) return 'marque'
  return 'insistant'
}

const CLASSES_CLAIR: Record<ReturnType<typeof palierEcheance>, string> = {
  discret: 'border-slate-200 bg-slate-50 text-slate-600',
  modere: 'border-amber-200 bg-amber-50 text-amber-800',
  marque: 'border-amber-300 bg-amber-100 text-amber-900 font-medium',
  insistant: 'border-red-300 bg-red-50 text-red-800 font-semibold',
}

/** Variante sombre (`LayoutScan`) — mêmes seuils, palette lisible sur fond noir sans éblouir. */
const CLASSES_SOMBRE: Record<ReturnType<typeof palierEcheance>, string> = {
  discret: 'border-slate-700 bg-slate-800/60 text-slate-300',
  modere: 'border-amber-800 bg-amber-950/40 text-amber-300',
  marque: 'border-amber-700 bg-amber-900/40 text-amber-200 font-medium',
  insistant: 'border-red-800 bg-red-950/40 text-red-300 font-semibold',
}

/**
 * « Votre démonstration se termine dans X jours » — discret à J-30, insistant
 * à J-2, et rien entre les deux qui ressemble à un interrupteur : l'intensité
 * pilote continûment la couleur, l'épaisseur du texte et la bordure.
 *
 * `sombre` : variante pour l'app de scan (`LayoutScan`), seule coque à fond
 * sombre — mêmes seuils, palette adaptée pour rester lisible sans éblouir un
 * contrôleur d'accès dans le noir.
 *
 * Rend `null` en dehors de la fenêtre J-30..J-1 : pas de bandeau tant qu'il
 * reste plus d'un mois, plus DU TOUT une fois bloqué (`EcranTenantBloquant`
 * a déjà pris toute la coque à ce moment-là, ce composant n'est jamais monté
 * en même temps que lui).
 */
export function BandeauEcheance({ sombre = false }: { readonly sombre?: boolean }) {
  const jours = useEcheanceTenant()
  if (jours === undefined) return null

  const intensite = intensiteEcheance(jours) ?? 0
  const palier = palierEcheance(intensite)
  const message = messageEcheance(jours)

  return (
    <div
      role="status"
      // `polite`, jamais `assertive` : un rappel quotidien ne doit pas couper
      // la parole à un lecteur d'écran — « lisible sans être pénible » vaut
      // aussi pour l'accessibilité, pas seulement pour l'œil.
      aria-live="polite"
      className={`flex items-center justify-center gap-1.5 border-b px-4 py-1.5 text-center text-xs ${
        sombre ? CLASSES_SOMBRE[palier] : CLASSES_CLAIR[palier]
      }`}
    >
      {palier === 'insistant' && (
        <span aria-hidden="true" className="text-sm leading-none">
          ⚠
        </span>
      )}
      {message}
    </div>
  )
}

/**
 * Le seul contact de l'éditeur, déjà affiché sur la vitrine
 * (`features/vitrine/SectionEditeur.tsx`) — jamais un e-mail inventé pour
 * cet écran : un visiteur qui compare les deux doit lire la même adresse.
 */
const EMAIL_CONTACT = 'info@ilygo.ch'
const LIEN_CONTACT = `mailto:${EMAIL_CONTACT}`

/** L'adresse du théâtre telle qu'un humain la recopie dans un e-mail — le sous-domaine, sans protocole ni port. */
function adresseTenant(): string {
  if (typeof window === 'undefined') return ''
  return window.location.hostname
}

function dateLisible(iso: string | undefined): string | undefined {
  if (iso === undefined) return undefined
  try {
    return new Date(iso).toLocaleDateString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  } catch {
    return undefined
  }
}

/**
 * Plein écran, remplace la coque entière (pas un bandeau) : sur un tenant
 * expiré ou suspendu, TOUTE requête échoue déjà — afficher la navigation
 * normale au-dessus d'un écran qui ne peut rien charger serait trompeur.
 */
export function EcranTenantBloquant({ etat }: { readonly etat: EtatTenantBloquant }) {
  const estExpire = etat.genre === 'expire'
  const date = estExpire ? dateLisible(etat.expireLe) : undefined

  return (
    <div className="flex min-h-dvh items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
        <p
          className={
            estExpire
              ? 'mb-2 text-xs font-semibold tracking-wide text-amber-700 uppercase'
              : 'mb-2 text-xs font-semibold tracking-wide text-red-700 uppercase'
          }
        >
          {estExpire ? 'Fin de la période d’essai' : 'Accès suspendu'}
        </p>

        <h1 className="mb-3 text-lg font-semibold text-slate-900">
          {estExpire
            ? 'Votre période d’essai est terminée'
            : 'Cet accès est temporairement suspendu'}
        </h1>

        <p className="mb-1 text-sm text-slate-600">{etat.message}</p>
        {estExpire && date !== undefined && (
          <p className="mb-4 text-sm text-slate-500">Échéance : {date}</p>
        )}
        {!estExpire && etat.motif !== undefined && etat.motif !== '' && (
          <p className="mb-4 text-sm text-slate-500">Motif : {etat.motif}</p>
        )}

        {/* ★ Promesse explicite : les données ne sont PAS perdues, et la
            récupération est un GESTE CONCRET, pas une formule creuse
            (RESTE-A-FAIRE, lot F point A — « présentées comme récupérables,
            en disant COMMENT »). Une démonstration en libre-service n'a par
            construction aucun « interlocuteur habituel » assigné : la
            renvoyer à un contact qu'elle n'a jamais eu serait une impasse. */}
        <div className="mb-6 rounded-md bg-slate-50 p-4 text-left text-sm text-slate-600">
          <p className="mb-1 font-medium text-slate-800">Vos données sont conservées.</p>
          <p className="mb-3">
            {estExpire
              ? 'Rien n’est supprimé à l’échéance : un délai de grâce protège vos saisons, ' +
                'vos abonnés et vos ventes le temps de prolonger votre essai ou de souscrire.'
              : 'Rien n’est supprimé pendant la suspension : vos données restent intactes ' +
                'et cet accès reprend dès que la situation est réglée.'}
          </p>
          <p>
            <span className="font-medium text-slate-800">
              {estExpire ? 'Pour prolonger ou souscrire : ' : 'Pour reprendre la main : '}
            </span>
            écrivez à{' '}
            <a href={LIEN_CONTACT} className="text-rideau-700 underline underline-offset-2">
              {EMAIL_CONTACT}
            </a>{' '}
            en indiquant l’adresse de votre théâtre ({adresseTenant()}). Vos saisons, vos abonnés
            et vos ventes réapparaissent tels quels — aucune ressaisie.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2">
          <a href={LIEN_CONTACT} className={classesBouton('primaire', 'sm')}>
            Écrire à {EMAIL_CONTACT}
          </a>
          <Button
            variante="secondaire"
            taille="sm"
            onClick={() => {
              window.location.reload()
            }}
          >
            Réessayer
          </Button>
        </div>
      </div>
    </div>
  )
}
